import { ApplicationRef, ComponentRef, DestroyRef, inject, Injectable, Injector, createComponent, Inject, DOCUMENT } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, interval } from 'rxjs';
import { UpdatePromptComponent } from '../../common/update-prompt/update-prompt.component';

@Injectable({ providedIn: 'root' })
export class ServiceWorkerService {
    private readonly destroyRef= inject(DestroyRef);
    private promptRef?: ComponentRef<UpdatePromptComponent>;

    constructor(
        private swUpdate: SwUpdate,
        private injector: Injector,
        private appRef: ApplicationRef,
        @Inject(DOCUMENT) private doc: Document
    ) {
        if (this.swUpdate.isEnabled) {
            interval(6 * 60 * 60 * 1000)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.swUpdate.checkForUpdate());

            this.listenForUpdates();
        }
    }

    private listenForUpdates(): void {
        this.swUpdate.versionUpdates
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
                map(evt => ({
                    current: evt.currentVersion,
                    available: evt.latestVersion
                }))
            )
            .subscribe(() => this.showUpdatePrompt());
    }

    private showUpdatePrompt(): void {
        if (this.promptRef) return;
        const compRef = createComponent(UpdatePromptComponent, {
            environmentInjector: this.appRef.injector,
        });

        this.appRef.attachView(compRef.hostView);

        const domElem = (compRef.hostView as any).rootNodes[0] as HTMLElement;
        this.doc.body.appendChild(domElem);

        this.promptRef = compRef;

        compRef.instance.onReload.subscribe(() => {
            this.swUpdate.activateUpdate().then(() => this.doc.location.reload());
        });

        compRef.instance.onDismiss.subscribe(() => {
            this.destroyPrompt();
        });
    }


    private destroyPrompt(): void {
        if (this.promptRef) {
            this.appRef.detachView(this.promptRef.hostView);
            this.promptRef.destroy();
            this.promptRef = undefined;
        }
    }
}
